import {useCallback, useRef, useState, useEffect} from 'react'

// Shared helpers for the react-select style components.
// It allows searching by subtitle in the options and adds some state helpers.
const useSelectEvents = ({onInit, onChange}) => {
  const wrapperRef = useRef(null)
  const selectRef = useRef(null)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [changeInProgress, setChangeInProgress] = useState(false)

  // Hack for bubbling 'change' events:
  // focus and blur events naturally bubble from the underlying ReactSelect component.
  // default/native change events do not bubble, so we have to manually (re-) dispatch them with {bubbles: true} here.
  // setting handlers on for these events has tricks:
  // $form.on('change', 'input, select')  -> will not work. the synthetic event comes from a div
  // $form.on('change', 'div') -> will work but will get an event per div that is bubbled through.
  // $form.on('change', 'div.js-slick-auto-save') -> will work correctly, assuming that exactly one div has that class.
  const handleChange = useCallback(
    (newValue) => {
      if (!wrapperRef.current)
        return

      // Update the DOM dataset so non-React code (Stimulus controllers) that
      // read `data-react-props` will see the new value immediately without
      // waiting for a remount. This keeps the legacy controller logic working
      // when react-select values change.
      try {
        const rp
          = wrapperRef.current.dataset && wrapperRef.current.dataset.reactProps
        const parsed = rp ? JSON.parse(rp) : {}
        // normalize newValue to match the server-side prop name used elsewhere
        parsed.defaultValue = Array.isArray(newValue)
          ? newValue
          : newValue || null
        wrapperRef.current.dataset.reactProps = JSON.stringify(parsed)
      } catch (e) {
        // ignore JSON errors and continue — we still dispatch the event
      }

      // Also update any hidden input inside the wrapper so legacy code that
      // reads input[type="hidden"] sees the new selection immediately.
      try {
        const hiddenInput = wrapperRef.current.querySelector(
          'input[type="hidden"]',
        )
        if (hiddenInput) {
          if (Array.isArray(newValue)) {
            // join multi values with comma (legacy expectation varies by field)
            hiddenInput.value = newValue
              .map((v) => (v && v.value) || v || '')
              .join(',')
          } else {
            hiddenInput.value = (newValue && newValue.value) || newValue || ''
          }
          // trigger native change on the hidden input as some legacy listeners read this directly
          hiddenInput.dispatchEvent(
            new Event('change', {bubbles: true, cancelable: true}),
          )
        }
      } catch (e) {
        // ignore errors
      }

      wrapperRef.current.dispatchEvent(
        new Event('change', {bubbles: true, cancelable: true}),
      )
      if (onChange && typeof onChange === 'function')
        onChange(newValue, wrapperRef.current)
    },
    [wrapperRef, onChange],
  )

  useEffect(() => {
    if (wrapperRef.current && onInit && typeof onInit === 'function')
      onInit(wrapperRef.current)
  }, [wrapperRef, onInit])

  return {
    handleChange,
    wrapperRef,
    selectRef,
    menuIsOpen,
    setMenuIsOpen,
    changeInProgress,
    setChangeInProgress,
  }
}

export default useSelectEvents
